import Box from '@mui/material/Box';
import { AnimatePresence, m } from 'motion/react';
import { useCallback, useRef } from 'react';

import { nudgeCardMotion, nudgeLauncherMotion, nudgePeekMotion } from '@/components/atoms';
import { TipCard, TipLauncher, TipPeekPill } from '@/components/molecules/scrollTip';
import { tipChrome } from '@/content/tips';
import { useScrollNudge } from '@/hooks/useScrollNudge';

import { STICKY_ACTION_BAR_HEIGHT } from './StickyActionBar';

/**
 * How wide the card is on a pointer device.
 *
 * 360px is about 30 characters of the body copy per line over three lines,
 * which is the shape the copy in `content/tips.ts` is written to. Wider and
 * the card starts competing with the page for the eye; narrower and the two
 * buttons stop fitting on one row and the card grows taller instead.
 */
const CARD_WIDTH = 360;

/**
 * Every stage occupies the same single grid cell, bottom-aligned.
 *
 * A grid overlay rather than absolute positioning, and the difference is worth
 * the two lines it costs. The launcher and the card have to share one anchor
 * point — the card is dropped onto the disc, so it only reads as *landing* on
 * it if the disc's bottom-left and the card's resting bottom-left are the same
 * pixel — and two stages are briefly on screen together during the swap.
 * Absolute children would need that point stated twice and kept in agreement;
 * one grid cell with `end` alignment states it once, and the wrapper sizes
 * itself to whichever stage is currently the tallest.
 *
 * `minWidth: 0` is the one line here that is not about position, and it is not
 * optional. A grid item's `min-width` defaults to `auto`, which resolves to its
 * min-content size, and a track is never sized below that — so the *track*
 * grows to whatever the widest unbreakable thing inside the stage is, however
 * narrow the wrapper has been told to be. The phone's collapsed offer has a
 * `noWrap` headline in it, whose min-content width is the whole sentence, so
 * the cell came out 396px wide inside a 366px wrapper and pushed the dismiss
 * button 21px off the right-hand edge, where the pill's own `overflow: hidden`
 * quietly cut it in half. The `minWidth: 0` chains inside the pill could not
 * prevent that on their own: this is the outermost link in that chain, and the
 * only one that the grid algorithm consults.
 */
const OVERLAY = { gridArea: '1 / 1', alignSelf: 'end', minWidth: 0 } as const;

/** The disc, which is 48px wide wherever the wrapper happens to be. */
const ANCHOR_DISC = { ...OVERLAY, justifySelf: 'start' } as const;

/**
 * The card and the collapsed pill, which take the wrapper's full width — the
 * fixed 360px from `md` up, and the viewport less its gutters below it.
 */
const ANCHOR_WIDE = { ...OVERLAY, justifySelf: 'stretch' } as const;

/** The card, plus the origin its exit collapses toward. Merged, or the
    spread loses it. */
const ANCHOR_CARD = { ...ANCHOR_WIDE, ...nudgeCardMotion.style } as const;

/**
 * A scroll tip: a brand-red disc that arrives once the visitor has started
 * scrolling, and the card that drops onto it.
 *
 * This file is the surface: where the three stages sit, how they trade places,
 * and where focus goes when they do. `useScrollNudge` decides which stage it
 * is in, `molecules/scrollTip` paints them, `atoms/scrollNudgeMotion.ts` holds
 * the movement, and `content/tips.ts` holds every word of it.
 *
 * ## Three stages, and the one object they are meant to look like
 *
 * `launcher`/`quiet` is a 48px disc, `peek` is the phone's collapsed offer,
 * `open` is the card. They share an anchor, a glyph and an accent edge, and
 * they animate into one another rather than cutting — so the disc, the bar and
 * the panel read as one thing in three states. That is what buys the card the
 * right to be assertive: the visitor can see where it came from and where it
 * goes back to.
 *
 * **Why the disc leads.** The card this replaced opened cold, frosted and
 * tinted to match whatever it floated over, in the bottom-left corner — the
 * lowest-attention region of a page. It was unnoticeable by construction. The
 * disc pings into that corner first and the card opens a beat later, into a
 * place the eye has already been sent.
 *
 * **Why the disc stays.** Closing the card collapses it back to the disc
 * instead of removing it, so the tip is one click away for the rest of the
 * session. A dismissal that costs the visitor nothing is a dismissal they can
 * make freely, which is the other half of being allowed to be louder.
 *
 * ## Where it sits
 *
 * Three things are already pinned to the bottom of the viewport, and the tip
 * has to be the fourth without being on top of any of them:
 *
 * - From md up, `FloatingWhatsApp` owns bottom-**right** (§60 K). So the tip
 *   takes bottom-left. Not an aesthetic preference: hiding the site's primary
 *   contact affordance in order to show a marketing message would be a poor
 *   trade, and two objects in the same corner is worse than either.
 * - Below md, `StickyActionBar` owns the full width of the bottom 72px
 *   (§60 L). Every stage clears it — its height plus the safe-area inset plus
 *   a gap — so the bar stays reachable with the card open.
 * - The header and both of those bars sit at `appBar`; this sits at
 *   `speedDial`, the layer the floating button is on. Deliberately *under* the
 *   chrome: if anything ever does overlap, the navigation should win.
 *
 * ## Focus follows the swap, and only when it was asked for
 *
 * Neither the launcher arriving nor the card opening itself touches focus: the
 * visitor is in the middle of reading something, and moving the caret to an
 * uninvited message is the keyboard equivalent of the thing this is trying not
 * to be.
 *
 * The two visitor-initiated transitions are the opposite case, and both move
 * focus, because a control that vanishes under the pointer leaves a keyboard
 * visitor nowhere:
 *
 * - **tapping the pill or the disc** moves focus into the card;
 * - **pressing the card's close button** moves it to the disc the card just
 *   collapsed into.
 *
 * Escape does not move focus, because focus was almost certainly never in the
 * card — it is handled on `document` precisely so that a visitor who never
 * tabbed into the card can still close it.
 *
 * Both are callback refs and not effects on the stage, because the node does
 * not exist when the stage changes: `AnimatePresence` mounts the arriving
 * stage while the leaving one is still on screen, and an effect reading a ref
 * here can run before the swap it is reacting to has produced a node. A
 * callback ref fires when the node actually attaches, whenever that is.
 */
export function ScrollNudge() {
  const { tip, stage, open, dismiss, engage } = useScrollNudge();

  /** Whether the pending open/dismiss was asked for; see above. */
  const wantsCardFocus = useRef(false);
  const wantsDiscFocus = useRef(false);

  const cardRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || !wantsCardFocus.current) return;
    wantsCardFocus.current = false;
    node.focus();
  }, []);

  const discRef = useCallback((node: HTMLButtonElement | null) => {
    if (!node || !wantsDiscFocus.current) return;
    wantsDiscFocus.current = false;
    node.focus();
  }, []);

  const openFromControl = useCallback(() => {
    wantsCardFocus.current = true;
    open();
  }, [open]);

  const dismissFromControl = useCallback(() => {
    wantsDiscFocus.current = true;
    dismiss();
  }, [dismiss]);

  if (!tip) return null;

  const showsDisc = stage === 'launcher' || stage === 'quiet';

  return (
    <Box
      sx={{
        position: 'fixed',
        // Under the header and both action bars; level with the floating
        // WhatsApp button it shares the bottom of the screen with.
        zIndex: (t) => t.zIndex.speedDial,
        // One cell, every stage in it. See OVERLAY.
        display: 'grid',
        // Below md: across the page, clear of the sticky bar and of the home
        // indicator underneath it. From md: the corner the floating button is
        // not in.
        left: { xs: 16, md: 24 },
        right: { xs: 16, md: 'auto' },
        bottom: {
          xs: `calc(${STICKY_ACTION_BAR_HEIGHT}px + env(safe-area-inset-bottom) + 12px)`,
          md: 24,
        },
        width: { xs: 'auto', md: CARD_WIDTH },
        // The wrapper is as tall as the tallest stage and outlives all of
        // them, so that a leaving stage has somewhere to animate out of. It
        // must not sit over the page swallowing clicks — only what is actually
        // painted takes the pointer.
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {/* No `mode`, so the arriving stage and the leaving one play together.
          `wait` would be the safer-looking choice and is wrong here: it holds
          the card's mount back until the disc has finished leaving, so the
          card would begin its fall over an empty corner and land on nothing.
          Overlapping them is the whole illusion — the disc is still there,
          and still shrinking, through the frames in which the card comes
          down on it. */}
      <AnimatePresence>
        {stage === 'peek' && (
          <m.div key="peek" {...nudgePeekMotion} style={ANCHOR_WIDE}>
            <TipPeekPill tip={tip} onOpen={openFromControl} onDismiss={dismissFromControl} />
          </m.div>
        )}

        {showsDisc && (
          <m.div key="disc" {...nudgeLauncherMotion} style={ANCHOR_DISC}>
            <TipLauncher
              ref={discRef}
              // The opening slide's eyebrow, because a disc has one label and
              // a deck has three messages. Slide 1 is the page's hook and the
              // slide the card opens on, so the disc announces what pressing
              // it would actually show.
              label={`${tipChrome.launcher}: ${tip.slides[0].eyebrow}`}
              // It pings on the way in and never again. At `quiet` the visitor
              // has already answered — a disc still asking after that is the
              // nag this feature is built to avoid, and the one thing a
              // repeating animation must not be allowed to become.
              pulse={stage === 'launcher' ? 'once' : false}
              onClick={openFromControl}
            />
          </m.div>
        )}

        {stage === 'open' && (
          <m.div key="card" {...nudgeCardMotion} style={ANCHOR_CARD}>
            <TipCard ref={cardRef} tip={tip} onDismiss={dismissFromControl} onEngage={engage} />
          </m.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
